import { useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { useProducerById } from '@/hooks/useMyProducers';
import { useProducerCapabilities } from '@/hooks/useProducerCapabilities';
import { useProducerEvents } from '@/hooks/useProducerEvents';
import { useProducerPilot } from '@/hooks/useProducerPilot';
import { useCoverAsset } from '@/hooks/useCoverAsset';
import { useProducerDashboardMetrics } from '@/hooks/useProducerDashboardMetrics';
import { useTrackOnce } from '@/lib/analytics/useTrackOnce';
import { track } from '@/lib/analytics';
import { useIsMobile } from '@/hooks/use-mobile';
import { DEMO_FARM } from '@/hooks/useMyFarms';
import {
  Info,
  AlertCircle,
  Loader2,
  Eye,
  CalendarDays,
  FileText,
  Image as ImageIcon,
  ChevronRight,
  Check,
  MapPin,
  CalendarCheck,
  Heart,
  Plus,
  ShoppingBag,
} from 'lucide-react';
import { getNextStep } from '@/lib/producer/getNextStep';
import { getOverviewPrimaryCta } from '@/lib/producer/overviewCta';
import { NextStepCard } from '@/components/producer/NextStepCard';
import { useProducerProducts } from '@/hooks/useProducerProducts';
import { useProducerOfferings } from '@/hooks/useProducerOfferings';
import { isFuture } from 'date-fns';
import { SharePromptsSection } from '@/features/share/prompts/SharePromptsSection';

/* ── Compact metric card (subdued variant for insights) ── */
function InsightCard({
  icon: Icon,
  label,
  value,
  isLoading,
  microcopy,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string | null;
  isLoading: boolean;
  microcopy?: string | null;
}) {
  return (
    <Card className="border-border/60 bg-card/80 shadow-none">
      <CardContent className="p-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-muted/60 shrink-0">
            <Icon className="h-3 w-3 text-muted-foreground/70" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-muted-foreground/70 leading-tight truncate">{label}</p>
            {isLoading ? (
              <Skeleton className="h-4 w-8 mt-0.5" />
            ) : (
              <p className="text-sm font-medium text-foreground/80 tabular-nums leading-tight">
                {value ?? '–'}
              </p>
            )}
          </div>
        </div>
        {microcopy && (
          <p className="text-[10px] text-muted-foreground/60 mt-1.5 leading-snug">{microcopy}</p>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Primary CTA card ── */
function PrimaryActionCard({
  title,
  subtitle,
  onClick,
}: {
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full rounded-xl bg-primary text-primary-foreground p-5 mb-6 text-left transition-all active:scale-[0.98] hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-base font-semibold leading-snug">{title}</p>
          <p className="text-sm opacity-80 mt-0.5">{subtitle}</p>
        </div>
        <Plus className="h-5 w-5 shrink-0 opacity-80 ml-3" />
      </div>
    </button>
  );
}

export default function ProducerFarmDashboard() {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { producer, isLoading, error, refetch } = useProducerById(farmId);
  const { meta, checklist, hasCapability, isLoading: capsLoading } = useProducerCapabilities(farmId);
  const { data: events } = useProducerEvents(farmId !== 'demo' ? farmId : undefined);
  const { products } = useProducerProducts(farmId !== 'demo' ? farmId : undefined);
  const { offerings: offeringKeys } = useProducerOfferings(farmId !== 'demo' ? farmId : undefined);
  const { isPilot } = useProducerPilot();
  const { asset: coverAsset, isLoading: coverLoading } = useCoverAsset('producer', farmId !== 'demo' ? farmId : undefined);
  const [rangeDays] = useState<7 | 30>(7);

  const { dbMetrics, trafficMetrics, isLoading: metricsLoading } = useProducerDashboardMetrics(
    farmId !== 'demo' ? producer?.id : undefined,
    rangeDays,
  );

  const isDemo = farmId === 'demo';
  const isFallbackMode = isDemo || (!producer && !isLoading);
  const producerName = isDemo ? DEMO_FARM.name : (producer?.name || 'Laddar...');
  const isPublished = producer?.status === 'public';

  // Analytics
  useTrackOnce('producer_dashboard_opened', {
    producer_id: farmId || '',
    is_public: isPublished ?? false,
  }, `dashboard-${farmId}`);

  // Derive upcoming & draft events
  const upcomingEvents = useMemo(() => {
    if (!events) return [];
    return events
      .filter(e => e.status === 'public' && isFuture(new Date(e.start_date)))
      .slice(0, 3);
  }, [events]);

  const draftEvents = useMemo(() => {
    if (!events) return [];
    return events.filter(e => e.status === 'draft').slice(0, 2);
  }, [events]);

  const publicEventCount = useMemo(() => {
    if (!events) return 0;
    return events.filter(e => e.status === 'public').length;
  }, [events]);

  // Checklist items
  const coverDone = Boolean(coverAsset);
  const descDone = Boolean(producer?.short_description?.trim());
  const hasContact = Boolean(producer?.email || producer?.phone || producer?.website);

  // Next step recommendation
  const nextStep = useMemo(() => {
    if (isDemo) return null;
    return getNextStep({
      hasCover: coverDone,
      upcomingCount: upcomingEvents.length,
      shopEnabled: Boolean(producer?.shop_enabled),
      productCount: products?.length ?? 0,
      hasContact,
      offeringKeys,
    });
  }, [isDemo, coverDone, upcomingEvents.length, producer?.shop_enabled, products, hasContact, offeringKeys]);

  // Dynamic CTA logic — offering-aware via canonical helper
  const ctaConfig = useMemo(() => {
    const firstDraft = draftEvents.length > 0
      ? { id: draftEvents[0].id, title: draftEvents[0].title }
      : null;
    return getOverviewPrimaryCta({
      offeringKeys,
      publicEventCount,
      firstDraft,
      basePath: `/app/producer/farm/${farmId}`,
    });
  }, [publicEventCount, draftEvents, farmId, offeringKeys]);

  const handlePrimaryCta = () => {
    track('producer_primary_cta_clicked', { cta_type: ctaConfig.ctaType, producer_id: farmId || '' });
    navigate(ctaConfig.href);
  };

  // Insight microcopy
  const viewsMicrocopy = useMemo(() => {
    if (metricsLoading) return null;
    const views = trafficMetrics?.producer_page_views ?? 0;
    if (views === 0 && isPublished) return 'Din sida är publicerad – dela länken för att nå fler 🌿';
    if (views > 0 && views < 10) return 'Ni har börjat synas – bra start';
    return null;
  }, [trafficMetrics, metricsLoading, isPublished]);

  const bookingsMicrocopy = useMemo(() => {
    if (metricsLoading) return null;
    const bookings = dbMetrics?.bookings_completed_count ?? 0;
    if (bookings === 0 && upcomingEvents.length > 0) return 'Tipsa om era aktiviteter för att få fler bokningar';
    if (bookings > 0) return 'Bra jobbat – ni har fått bokningar den här veckan';
    return null;
  }, [dbMetrics, metricsLoading, upcomingEvents.length]);

  const savedMicrocopy = useMemo(() => {
    if (metricsLoading) return null;
    const saved = dbMetrics?.saved_producer_count ?? 0;
    if (saved > 0) return `Någon har sparat er ❤️`;
    return null;
  }, [dbMetrics, metricsLoading]);

  if (isLoading && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Laddar..." title="Översikt">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  if (error && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Fel" title="Översikt">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {(error as Error).message?.includes('permission')
              ? 'Du har inte behörighet att se den här gården.'
              : 'Kunde inte ladda gården. Försök igen.'}
          </AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button variant="outline" onClick={() => refetch()}>Försök igen</Button>
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout farmId={farmId || 'demo'} producerName={producerName} title="Översikt">
      {/* Demo alert */}
      {isFallbackMode && (
        <Alert className="border-border bg-muted mb-6">
          <Info className="h-4 w-4 text-primary" />
          <AlertDescription className="text-foreground">
            <strong>Demo-läge:</strong> Du tittar på en förhandsgranskning.
          </AlertDescription>
        </Alert>
      )}

      {/* ── Status row (compact) ── */}
      <section className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant={isPublished ? 'default' : 'secondary'}>
              {isPublished ? 'Publicerad' : 'Utkast'}
            </Badge>
            {producer?.slug && (
              <Button variant="ghost" size="sm" className="text-xs h-7" asChild>
                <a href={`/producer/${producer.slug}?preview=1`} target="_blank" rel="noopener noreferrer">
                  <Eye className="h-3.5 w-3.5 mr-1" />
                  Förhandsvisa
                </a>
              </Button>
            )}
          </div>
          <Button variant="ghost" size="sm" className="text-xs h-7" asChild>
            <Link to={`/app/producer/farm/${farmId}/plats`}>
              <MapPin className="h-3.5 w-3.5 mr-1" />
              Er sida
            </Link>
          </Button>
        </div>
      </section>

      {/* ── 1. Dynamic Primary CTA (mobile only — desktop shows inline) ── */}
      {!isDemo && isMobile && (
        <PrimaryActionCard
          title={ctaConfig.title}
          subtitle={ctaConfig.subtitle}
          onClick={handlePrimaryCta}
        />
      )}

      {/* Desktop: show CTA inline as a standard button */}
      {!isDemo && !isMobile && (
        <div className="mb-6">
          <Button onClick={handlePrimaryCta} size="lg" className="gap-2">
            <Plus className="h-4 w-4" />
            {ctaConfig.title}
          </Button>
          <p className="text-xs text-muted-foreground mt-1">{ctaConfig.subtitle}</p>
        </div>
      )}

      {/* ── 1b. Nästa steg (calm guidance) ── */}
      {nextStep && (
        <NextStepCard step={nextStep} basePath={`/app/producer/farm/${farmId}`} />
      )}

      {/* ── 1c. Share Prompts (Tips från Farmly) ── */}
      {!isDemo && producer && (
        <SharePromptsSection
          producerId={producer.id}
          producerName={producerName}
          producerSlug={producer.slug}
        />
      )}

      {/* ── 2. Det ni erbjuder just nu ── */}
      <section className="mb-8">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Det ni erbjuder just nu</h2>

        {upcomingEvents.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Kommande</p>
            <div className="space-y-2">
              {upcomingEvents.map(ev => (
                <Link
                  key={ev.id}
                  to={`/app/producer/farm/${farmId}/events/${ev.id}/edit`}
                  className="flex items-center justify-between py-2.5 px-3 rounded-md border border-border hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="default" className="text-[10px] px-1.5 py-0">Publicerad</Badge>
                    <span className="text-sm text-foreground">{ev.title}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {draftEvents.length > 0 && (
          <div className="mb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Utkast</p>
            <div className="space-y-2">
              {draftEvents.map(ev => (
                <Link
                  key={ev.id}
                  to={`/app/producer/farm/${farmId}/events/${ev.id}/edit`}
                  className="flex items-center justify-between py-2.5 px-3 rounded-md border border-dashed border-border hover:bg-muted/40 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Utkast</Badge>
                    <span className="text-sm text-muted-foreground">{ev.title}</span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {upcomingEvents.length === 0 && draftEvents.length === 0 && (
          <p className="text-sm text-muted-foreground">Ni har inga aktiviteter ännu.</p>
        )}
      </section>

      {/* ── 3. Snabbåtgärder (secondary, sorted by relevance) ── */}
      <section className="mb-8">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
          Snabbåtgärder
        </h2>
        <div className="space-y-2">
          {(() => {
            const basePath = `/app/producer/farm/${farmId}`;
            const actions = [
              { key: 'cover', href: `${basePath}/plats`, icon: ImageIcon, label: 'Lägg till omslagsbild', show: !coverDone, dashed: true },
              { key: 'events', href: `${basePath}/events`, icon: CalendarCheck, label: 'Se bokningar', show: true, dashed: false },
              { key: 'products', href: `${basePath}/shop`, icon: ShoppingBag, label: 'Hantera gårdsbutik', show: true, dashed: false },
              { key: 'contact', href: `${basePath}/plats`, icon: FileText, label: 'Redigera berättelse', show: true, dashed: false },
            ].filter(a => a.show);

            // Sort: action matching nextStep first
            if (nextStep) {
              actions.sort((a, b) => {
                if (a.key === nextStep.key) return -1;
                if (b.key === nextStep.key) return 1;
                return 0;
              });
            }

            return actions.map(action => (
              <Link
                key={action.key}
                to={action.href}
                className={`flex items-center justify-between py-2.5 px-3 rounded-md border hover:bg-muted/40 transition-colors ${action.dashed ? 'border-dashed border-border' : 'border-border'}`}
              >
                <div className="flex items-center gap-2">
                  <action.icon className="h-4 w-4 text-muted-foreground" />
                  <span className={`text-sm ${action.dashed ? 'text-muted-foreground' : 'text-foreground'}`}>{action.label}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ));
          })()}
        </div>
      </section>

      {/* ── 4. Insikter (subdued, bottom) ── */}
      {!isDemo && (
        <section className="mb-8">
          <h2 className="text-xs font-medium text-muted-foreground/70 uppercase tracking-wide mb-2">
            Insikter (senaste 7 dagarna)
          </h2>
          <div className="grid grid-cols-2 gap-2">
            <InsightCard
              icon={Eye}
              label="Sidvisningar"
              value={trafficMetrics?.producer_page_views ?? null}
              isLoading={metricsLoading}
              microcopy={viewsMicrocopy}
            />
            <InsightCard
              icon={CalendarCheck}
              label="Bokningar"
              value={dbMetrics?.bookings_completed_count ?? null}
              isLoading={metricsLoading}
              microcopy={bookingsMicrocopy}
            />
            <InsightCard
              icon={CalendarDays}
              label="Aktiva aktiviteter"
              value={upcomingEvents.length}
              isLoading={false}
            />
            <InsightCard
              icon={Heart}
              label="Sparade"
              value={dbMetrics?.saved_producer_count ?? null}
              isLoading={metricsLoading}
              microcopy={savedMicrocopy}
            />
          </div>
        </section>
      )}
    </ProducerLayout>
  );
}
